import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { BookOpenText, ChevronDown, ChevronLeft, ChevronRight, Download, FileText, Lock, Printer, Search, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { StoreHeader } from "../components/StoreHeader";
import { CALCULATORS } from "../components/manual/Calculators";
import { FrontMatter } from "../components/manual/FrontMatter";
import "../styles/manual.css";

const MD = {
  h2: ({ children }) => <h2 className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mt-2 mb-6 pb-3 border-b border-[#D8B23E]/30">{children}</h2>,
  h3: ({ children }) => <h3 className="font-serif text-lg text-[#D8B23E] mt-8 mb-3">{children}</h3>,
  p: ({ children }) => <p className="font-body text-[15px] leading-[1.85] text-[#D9D9D2] mb-4 text-justify">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-6 mb-4 space-y-1.5 text-[15px] text-[#D9D9D2]">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-6 mb-4 space-y-1.5 text-[15px] text-[#D9D9D2]">{children}</ol>,
  table: ({ children }) => <div className="overflow-x-auto my-5 border border-[#D8B23E]/30 rounded-sm"><table className="w-full text-[12.5px]">{children}</table></div>,
  thead: ({ children }) => <thead className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.12em] text-[9.5px]">{children}</thead>,
  th: ({ children }) => <th className="text-left px-3 py-2">{children}</th>,
  td: ({ children }) => <td className="px-3 py-2 border-t border-white/10 text-[#D9D9D2] align-top">{children}</td>,
  strong: ({ children }) => <strong className="text-[#F5F5F0] font-semibold">{children}</strong>,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-[#D8B23E] pl-4 italic text-[#C9C9C2] my-4">{children}</blockquote>,
};

function Sidebar({ parts, frontMatter, current, onPick, query, setQuery, results, onClose }) {
  const [open, setOpen] = useState(() => new Set(parts.map((p) => p.num)));
  const toggle = (n) => setOpen((s) => { const x = new Set(s); x.has(n) ? x.delete(n) : x.add(n); return x; });
  return (
    <aside className="manual-sidebar w-[320px] shrink-0 border-r border-white/10 bg-[#070706] h-[calc(100vh-80px)] sticky top-20 overflow-y-auto print:hidden" data-testid="manual-sidebar">
      <div className="p-4 border-b border-white/10 sticky top-0 bg-[#070706] z-10">
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A82]" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search clauses, terms, tables…"
                 data-testid="manual-search-input"
                 className="w-full bg-black/60 border border-white/15 pl-9 pr-8 py-2 text-sm text-[#F5F5F0] focus:border-[#D8B23E] outline-none" />
          {query && <button onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8A8A82]" data-testid="manual-search-clear"><X size={13} /></button>}
        </div>
      </div>
      {query.length >= 3 ? (
        <div className="p-3 space-y-1" data-testid="manual-search-results">
          <div className="font-grotesk text-[8px] uppercase tracking-[0.28em] text-[#8A8A82] px-2 pb-2">{results.length} matching clause{results.length === 1 ? "" : "s"}</div>
          {results.map((r) => (
            <button key={r.id} onClick={() => { onPick(r.id); onClose?.(); }} data-testid={`manual-result-${r.id}`}
                    className="w-full text-left px-3 py-2 rounded-sm hover:bg-[#D8B23E]/10 border border-transparent hover:border-[#D8B23E]/30">
              <div className="text-[12px] text-[#F5F5F0]"><span className="font-mono text-[#D8B23E] mr-2">{r.id}</span>{r.title}</div>
              {r.snippets[0] && <div className="text-[10.5px] text-[#8A8A82] italic mt-0.5 line-clamp-2">…{r.snippets[0]}…</div>}
              {r.hits > 0 && <div className="text-[9px] font-grotesk uppercase tracking-[0.2em] text-[#8A8A82] mt-0.5">{r.hits} hit{r.hits === 1 ? "" : "s"}</div>}
            </button>
          ))}
        </div>
      ) : (
        <nav className="p-3" data-testid="manual-toc">
          <div className="mb-3">
            <div className="font-grotesk text-[9px] uppercase tracking-[0.26em] text-[#D8B23E] px-2 py-2">Front Matter</div>
            {frontMatter.map((f) => (
              <button key={f.id} onClick={() => { onPick(f.id); onClose?.(); }} data-testid={`manual-nav-${f.id}`}
                      className={`w-full text-left px-3 py-1.5 text-[12px] rounded-sm border-l-2 transition-colors ${current === f.id ? "border-[#D8B23E] bg-[#D8B23E]/10 text-[#F5F5F0]" : "border-transparent text-[#A9A9A2] hover:text-[#F5F5F0] hover:bg-white/5"}`}>{f.title}</button>))}
          </div>
          {parts.map((p) => (
            <div key={p.num} className="mb-2">
              <button onClick={() => toggle(p.num)} data-testid={`manual-part-${p.num}`}
                      className="w-full flex items-center justify-between px-2 py-2 text-left">
                <span className="font-grotesk text-[9px] uppercase tracking-[0.26em] text-[#D8B23E]">Part {p.num} · {p.title}</span>
                <ChevronDown size={12} className={`text-[#8A8A82] transition-transform ${open.has(p.num) ? "" : "-rotate-90"}`} />
              </button>
              <AnimatePresence initial={false}>
                {open.has(p.num) && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    {p.chapters.map((ch) => (
                      <div key={ch.num} className="pl-2 mb-1">
                        <div className="font-serif text-[12.5px] text-[#C9C9C2] px-2 pt-2 pb-1">Ch. {ch.num} — {ch.title}</div>
                        {ch.sections.map((s) => (
                          <button key={s.id} onClick={() => { onPick(s.id); onClose?.(); }} data-testid={`manual-nav-${s.id}`}
                                  className={`w-full text-left px-3 py-1.5 text-[12px] rounded-sm flex items-start gap-2 border-l-2 transition-colors ${
                                    current === s.id ? "border-[#D8B23E] bg-[#D8B23E]/10 text-[#F5F5F0]" : "border-transparent text-[#A9A9A2] hover:text-[#F5F5F0] hover:bg-white/5"}`}>
                            <span className="font-mono text-[10.5px] text-[#D8B23E] shrink-0 w-9">{s.id}</span>
                            <span className="flex-1">{s.title}{!s.drafted && <span className="ml-1 text-[8px] font-grotesk uppercase tracking-[0.2em] text-[#8A8A82]">· drafting</span>}</span>
                            {s.calculator && <span className="text-[8px] font-grotesk uppercase tracking-[0.2em] text-[#2FB999] shrink-0">calc</span>}
                          </button>
                        ))}
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </nav>
      )}
    </aside>
  );
}

function Masthead({ meta, drafted, total, onDownload }) {
  return (
    <div className="border-b border-white/10 bg-[#0A0906] print:hidden">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="font-grotesk text-[9px] uppercase tracking-[0.32em] text-[#D8B23E]">Vinci Inc. · Internal Administrative Reference · Admin only</div>
          <h1 className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mt-1">Vinci Operations Manual <span className="text-[#D8B23E]">— Official Edition 2026</span></h1>
          <div className="text-[11px] text-[#8A8A82] font-body mt-1" data-testid="manual-masthead-meta">
            Version 1.0 · October 2026 · Effective {meta?.effective} · {drafted}/{total} clauses issued · GSTIN {meta?.identifiers?.gstin} · IEC {meta?.identifiers?.iec}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => window.print()} data-testid="manual-print-btn"
                  className="inline-flex items-center gap-2 border border-white/20 text-[#C9C9C2] px-4 py-2 font-grotesk text-[9px] uppercase tracking-[0.24em] hover:border-[#D8B23E] hover:text-[#D8B23E] transition-colors">
            <Printer size={12} /> Print clause
          </button>
          <button onClick={onDownload} data-testid="manual-download-pdf-btn"
                  className="inline-flex items-center gap-2 bg-[#D8B23E] text-black px-4 py-2 font-grotesk text-[9px] uppercase tracking-[0.24em] hover:bg-[#E5C450] transition-colors">
            <Download size={12} /> Download PDF
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Manual() {
  const { api } = useAuth();
  const { sectionId } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [denied, setDenied] = useState(null);
  const [section, setSection] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [showDefs, setShowDefs] = useState(false);

  useEffect(() => {
    api.get("/manual/toc").then((r) => setMeta(r.data)).catch((e) => setDenied(e.response?.data?.detail || "Access restricted"));
  }, [api]);

  useEffect(() => {
    if (!meta) return;
    if (!sectionId || sectionId.startsWith("fm-")) { setSection(null); window.scrollTo({ top: 0 }); return; }
    const id = sectionId;
    api.get(`/manual/section/${id}`).then((r) => { setSection(r.data); window.scrollTo({ top: 0, behavior: "smooth" }); })
       .catch(() => toast.error("Clause not found"));
  }, [api, meta, sectionId]);

  useEffect(() => {
    if (query.trim().length < 3) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/manual/search?q=${encodeURIComponent(query)}`).then((r) => setResults(r.data.results)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [api, query]);

  const pick = useCallback((id) => navigate(`/admin/manual/${id}`), [navigate]);
  const total = meta?.total_sections || 0;
  const Calc = section?.calculator ? CALCULATORS[section.calculator] : null;

  const download = async () => {
    try {
      const r = await api.get("/manual/pdf/latest", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = Object.assign(document.createElement("a"), { href: url, download: "Vinci-Operations-Manual-2026-27.pdf" });
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Compiled edition downloaded");
    } catch (e) {
      const d = e.response?.status === 404 ? "No compiled edition has been issued yet — the Office of the Manual will notify you." : "Download failed";
      toast.error(d);
    }
  };

  if (denied) {
    return (
      <div className="min-h-screen bg-[#050505] text-[#F5F5F0]">
        <StoreHeader />
        <div className="max-w-xl mx-auto px-6 py-28 text-center" data-testid="manual-access-denied">
          <Lock size={28} className="mx-auto text-[#D8B23E]" />
          <h1 className="font-serif text-3xl mt-6">Internal Publication</h1>
          <p className="text-[#C9C9C2] font-body mt-4 leading-relaxed">{denied}</p>
          <Link to="/admin" className="inline-block mt-8 bg-[#D8B23E] text-black px-6 py-3 font-grotesk text-[9px] uppercase tracking-[0.26em]">Return to Admin</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F5F0] manual-root" data-testid="manual-page">
      <StoreHeader />
      <Masthead meta={meta} drafted={meta?.drafted_sections || 0} total={total} onDownload={download} />
      <div className="max-w-[1600px] mx-auto flex">
        {meta && <Sidebar parts={meta.parts} frontMatter={meta.front_matter.sections} current={sectionId?.startsWith("fm-") ? sectionId : section?.id} onPick={pick} query={query} setQuery={setQuery} results={results} />}
        <main className="flex-1 min-w-0 px-6 md:px-14 py-10 manual-reading" data-testid="manual-reading-pane">
          {(!sectionId || sectionId.startsWith("fm-")) && meta ? (
            <motion.article key={sectionId || "fm-cover"} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-[900px]" data-testid="manual-front-matter">
              <div className="print-header hidden print:block"><div>Vinci Operations Manual — Official Edition 2026 · Front Matter</div><div>Vinci Incorporation · Confidential — For Authorised Personnel Only</div></div>
              <FrontMatter id={sectionId || "fm-cover"} fm={meta.front_matter} parts={meta.parts} onPick={pick} />
              <div className="mt-12 pt-6 border-t border-white/10 flex justify-between print:hidden">
                {(() => { const ids = meta.front_matter.sections.map((f) => f.id); const i = ids.indexOf(sectionId || "fm-cover");
                  const prev = i > 0 ? meta.front_matter.sections[i - 1] : null; const next = i < ids.length - 1 ? meta.front_matter.sections[i + 1] : { id: "1.1", title: "Short Title, Commencement and Extent" };
                  return (<>
                    {prev ? <button onClick={() => pick(prev.id)} data-testid="manual-prev-btn" className="text-sm text-[#C9C9C2] hover:text-[#D8B23E]"><ChevronLeft size={10} className="inline" /> {prev.title}</button> : <span />}
                    <button onClick={() => pick(next.id)} data-testid="manual-next-btn" className="text-sm text-[#C9C9C2] hover:text-[#D8B23E]">{next.title} <ChevronRight size={10} className="inline" /></button>
                  </>); })()}
              </div>
            </motion.article>
          ) : section ? (
            <motion.article key={section.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="max-w-[860px]">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-6 print:hidden">
                <div className="font-grotesk text-[9px] uppercase tracking-[0.3em] text-[#8A8A82]" data-testid="manual-breadcrumb">
                  Part {section.part} — {section.part_title} · Chapter {section.chapter} — {section.chapter_title}
                </div>
                <button onClick={() => setShowDefs((v) => !v)} data-testid="manual-definitions-toggle" className="inline-flex items-center gap-1.5 text-[9px] font-grotesk uppercase tracking-[0.24em] text-[#D8B23E]">
                  <BookOpenText size={11} /> Definitions
                </button>
              </div>
              <AnimatePresence>
                {showDefs && meta && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden mb-6" data-testid="manual-definitions-panel">
                    <div className="rounded-sm border border-[#D8B23E]/30 bg-[#0A0906] p-5 max-h-72 overflow-y-auto grid md:grid-cols-2 gap-x-6 gap-y-2">
                      {meta.definitions.map((d) => <p key={d.term} className="text-[12px] text-[#C9C9C2]"><b className="text-[#F5F5F0]">{d.term}</b> — {d.meaning}</p>)}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <div className="print-header hidden print:block">
                <div>Vinci Operations Manual 2026-27 · Part {section.part} — {section.part_title}</div>
                <div>Vinci Incorporation · Confidential — For Authorised Personnel Only</div>
              </div>
              {section.markdown ? (
                <div data-testid="manual-section-body" className="manual-md">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{section.markdown}</ReactMarkdown>
                </div>
              ) : (
                <div data-testid="manual-section-pending">
                  <h2 className="font-serif text-3xl mb-6">{section.id} {section.title}</h2>
                  <div className="rounded-sm border border-dashed border-[#D8B23E]/40 p-6 text-[#C9C9C2] font-body">
                    <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#D8B23E] mb-3">Clause under drafting by the Secretariat</div>
                    <ul className="list-disc pl-5 space-y-1 text-sm">{section.outline.map((o, i) => <li key={i}>{o}</li>)}</ul>
                  </div>
                </div>
              )}
              {Calc && <Calc api={api} />}
              <div className="mt-12 pt-6 border-t border-white/10 flex items-center justify-between gap-4 print:hidden">
                {section.prev ? (
                  <button onClick={() => pick(section.prev.id)} data-testid="manual-prev-btn" className="group text-left">
                    <div className="font-grotesk text-[8px] uppercase tracking-[0.28em] text-[#8A8A82]"><ChevronLeft size={10} className="inline" /> Previous clause</div>
                    <div className="text-sm text-[#C9C9C2] group-hover:text-[#D8B23E] transition-colors"><span className="font-mono text-[#D8B23E] mr-2">{section.prev.id}</span>{section.prev.title}</div>
                  </button>) : <span />}
                {section.next ? (
                  <button onClick={() => pick(section.next.id)} data-testid="manual-next-btn" className="group text-right">
                    <div className="font-grotesk text-[8px] uppercase tracking-[0.28em] text-[#8A8A82]">Next clause <ChevronRight size={10} className="inline" /></div>
                    <div className="text-sm text-[#C9C9C2] group-hover:text-[#D8B23E] transition-colors"><span className="font-mono text-[#D8B23E] mr-2">{section.next.id}</span>{section.next.title}</div>
                  </button>) : <span />}
              </div>
              <div className="mt-6 text-[10px] text-[#8A8A82] font-grotesk uppercase tracking-[0.22em] flex items-center gap-2" data-testid="manual-section-meta">
                <FileText size={10} /> Clause {section.id} · {section.words || 0} words · version {section.version || 0} · {section.status}
              </div>
            </motion.article>
          ) : (
            <div className="py-20 text-center text-[#8A8A82] font-body italic">Opening the Manual…</div>
          )}
        </main>
      </div>
    </div>
  );
}
